import { URLS, HTTP_METHODS, HEADERS_CONTENT_TYPE } from 'modules/api/constants';
import handleRequest from 'modules/api/common';
import { CancelFunctions } from 'components/common/types';
import {
  DirectSalesFilteredCampaignsRequestResponse,
  GetFiltersRequestData,
} from 'components/common/types/DirectSalesCampaign.types';
import { DEFAULT_DIRECT_SALES_DEALS_CAMPAIGN_STATUS_CODES } from 'components/pages/Deals/DirectSalesDeals/Filters/campaignStatusFilterOptions';
import { DirectSalesDealManagementFilters } from 'components/pages/Deals/DirectSalesDeals/Filters/Filters.types';
import { format, startOfDay } from 'date-fns';
import { formatEndDateWithoutMilliseconds, formatStartDateWithoutMilliseconds } from 'utils/schedule';

type SearchEntry = GetFiltersRequestData['search'][number];
type SearchFieldOption = SearchEntry['field'];
type SearchOperationOption = SearchEntry['operation'];
type CampaignsTableSort = Pick<GetFiltersRequestData, 'sortBy' | 'sortDir'>;

interface FilterMapping {
  field: SearchFieldOption;
  operation: SearchOperationOption;
  getValue: (filters: DirectSalesDealManagementFilters) => SearchEntry['value'] | undefined;
}

const getMediaTypeValue = (mediaType: string): string[] =>
  mediaType === 'DIGITAL_AND_PAPER' ? ['DIGITAL', 'PAPER'] : [mediaType];

const FILTER_MAPPINGS: FilterMapping[] = [
  {
    field: 'START_DATE',
    operation: 'GREATER_OR_EQUAL',
    getValue: (filters) =>
      filters.startDate ? formatStartDateWithoutMilliseconds(startOfDay(new Date(filters.startDate))) : undefined,
  },
  {
    field: 'END_DATE',
    operation: 'LESS_OR_EQUAL',
    getValue: (filters) =>
      filters.endDate ? formatEndDateWithoutMilliseconds(new Date(filters.endDate), filters.endDate) : undefined,
  },
  { field: 'ADVERTISER', operation: 'EQUAL', getValue: (filters) => filters.advertiser?.name },
  { field: 'BRAND', operation: 'EQUAL', getValue: (filters) => filters.brand?.name },
  { field: 'PRODUCT_CATEGORY', operation: 'EQUAL', getValue: (filters) => filters.productCategory?.name },
  { field: 'SPECIALIST', operation: 'EQUAL', getValue: (filters) => filters.specialist?.name },
  { field: 'AGENCY', operation: 'EQUAL', getValue: (filters) => filters.agency?.name },
  {
    field: 'CREATED_AT',
    operation: 'EQUAL',
    getValue: (filters) => (filters.createdAt ? format(filters.createdAt, 'yyyy-MM-dd') : undefined),
  },
  {
    field: 'DROP_DATE',
    operation: 'EQUAL',
    getValue: (filters) => (filters.dropDate ? format(filters.dropDate, 'yyyy-MM-dd') : undefined),
  },
  { field: 'UPDATED_BY', operation: 'CONTAINS', getValue: (filters) => filters.updatedBy || undefined },
  { field: 'CREATED_BY', operation: 'CONTAINS', getValue: (filters) => filters.createdBy || undefined },
  { field: 'CAMPAIGN_NAME', operation: 'CONTAINS', getValue: (filters) => filters.campaignName || undefined },
  {
    field: 'CAMPAIGN_LOOKUP_KEY',
    operation: 'CONTAINS',
    getValue: (filters) => filters.campaignReference || undefined,
  },
  { field: 'SALES_TEAM', operation: 'EQUAL', getValue: (filters) => filters.salesTeam?.name },
  { field: 'SALES_PERSON', operation: 'EQUAL', getValue: (filters) => filters.salesPerson?.name },
  { field: 'ADMIN_PERSON', operation: 'EQUAL', getValue: (filters) => filters.adminPerson?.name },
  {
    field: 'CAMPAIGN_MEDIA_TYPE',
    operation: 'EQUAL',
    getValue: (filters) => (filters.campaignMediaType ? getMediaTypeValue(filters.campaignMediaType) : undefined),
  },
];

const buildOptionalSearchFilters = (filters: DirectSalesDealManagementFilters): SearchEntry[] =>
  FILTER_MAPPINGS.reduce<SearchEntry[]>((entries, { field, operation, getValue }) => {
    const value = getValue(filters);
    if (value !== undefined) entries.push({ field, operation, value });
    return entries;
  }, []);

const buildCampaignStatusFilter = (filters?: DirectSalesDealManagementFilters): SearchEntry[] => {
  const statuses =
    filters?.campaignStatus === undefined ? DEFAULT_DIRECT_SALES_DEALS_CAMPAIGN_STATUS_CODES : filters.campaignStatus;

  return statuses.length > 0 ? [{ field: 'CAMPAIGN_STATUS', operation: 'IN', value: statuses }] : [];
};

const getFiltersRequestData = (
  publisherId: string,
  filters?: DirectSalesDealManagementFilters,
  sort?: CampaignsTableSort,
): GetFiltersRequestData => ({
  search: [
    { field: 'PUBLISHER', operation: 'EQUAL', value: publisherId },
    ...(filters ? buildOptionalSearchFilters(filters) : []),
    ...buildCampaignStatusFilter(filters),
  ],
  sortBy: sort?.sortBy,
  sortDir: sort?.sortDir,
});

export const getDirectSalesFilteredCampaignsTable = ({
  externalPublisherId,
  cancelFunctions,
  filters,
  page,
  size,
  sort,
}: {
  externalPublisherId: string;
  cancelFunctions: CancelFunctions;
  page: number;
  size: number;
  filters?: DirectSalesDealManagementFilters;
  sort?: CampaignsTableSort;
}): Promise<DirectSalesFilteredCampaignsRequestResponse> => {
  const requestData = getFiltersRequestData(externalPublisherId, filters, sort);

  const query = `page=${page}&size=${size}`;

  const reqOptions = {
    url: URLS().FETCH_DIRECT_SALES_FILTERED_CAMPAIGNS_TABLE(externalPublisherId, query),
    method: HTTP_METHODS.POST,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    showLoader: true,
    data: requestData,
  };

  return handleRequest(reqOptions, cancelFunctions, 'FETCH_DIRECT_SALES_FILTERED_CAMPAIGNS_TABLE');
};
